import React, { memo, useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import { Button, type ButtonVariant } from "../button/Button";

// Hook to detect screen size for responsive behavior
const useResponsiveHeader = () => {
  const [screenSize, setScreenSize] = useState<"mobile" | "tablet" | "desktop">(
    "desktop"
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkSize = () => {
      if (window.innerWidth < 640) {
        setScreenSize("mobile");
      } else if (window.innerWidth < 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  return screenSize;
};

// ============================================
// TYPES
// ============================================

/**
 * Configuration for a single action button in the header
 */
export interface HeaderActionButton {
  /**
   * Unique key for the button (used for React key prop)
   */
  key: string;

  /**
   * Label text or ReactNode for the button
   */
  label: React.ReactNode;

  /**
   * Click handler for the button
   */
  onClick: () => void;

  /**
   * Button variant
   * @default "teal"
   */
  variant?: ButtonVariant;

  /**
   * Optional icon to display after the label
   */
  icon?: React.ReactNode;

  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the button is in a loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Additional className for the button
   */
  className?: string;
}

export interface CustomHeaderProps {
  /**
   * Main title displayed in the center
   * Can be a string or ReactNode for custom rendering
   */
  title: React.ReactNode;

  /**
   * Optional subtitle displayed below the title
   */
  subtitle?: React.ReactNode;

  /**
   * Handler for back button click
   * Required - the header always needs a way to navigate back
   */
  onBack: () => void;

  /**
   * Label for the back button
   * @default "Back"
   */
  backLabel?: string;

  /**
   * Whether to show the back button
   * @default true
   */
  showBackButton?: boolean;

  /**
   * Custom icon for the back button
   * @default ArrowLeft from lucide-react
   */
  backIcon?: React.ReactNode;

  /**
   * Handler for the action button click
   * If not provided, no action button will be shown
   */
  onAction?: () => void;

  /**
   * Label for the action button
   * Can be a string or ReactNode for custom content (e.g., with icons)
   * Required if onAction is provided
   */
  actionLabel?: React.ReactNode;

  /**
   * Icon to display after the action label
   * Commonly used for next/continue icons
   */
  actionIcon?: React.ReactNode;

  /**
   * Variant for the action button
   * @default "teal"
   */
  actionVariant?: ButtonVariant;

  /**
   * Whether the action is in a loading state
   * @default false
   */
  isLoading?: boolean;

  /**
   * Whether the action button is disabled
   * @default false
   */
  isActionDisabled?: boolean;

  /**
   * Whether to disable the back button
   * Useful when an action is in progress
   * @default false
   */
  isBackDisabled?: boolean;

  /**
   * Array of action buttons to display on the right side
   * Takes precedence over single action props (onAction, actionLabel, etc.)
   * When provided, title remains centered regardless of button count
   */
  actionButtons?: HeaderActionButton[];

  /**
   * Optional content to display to the left of the title (e.g., an icon)
   */
  leftContent?: React.ReactNode;

  /**
   * Custom class name for the header container
   */
  className?: string;

  /**
   * Custom styles for the header container
   */
  style?: React.CSSProperties;

  /**
   * Background color of the header
   * @default colors.primarySlate
   */
  backgroundColor?: string;

  /**
   * Whether to add right placeholder when no action button
   * Helps maintain layout symmetry
   * @default true
   */
  balanceLayout?: boolean;

  /**
   * Minimum width for the back/action buttons area
   * Used for layout balancing
   * @default "80px"
   */
  buttonMinWidth?: string;

  /**
   * Alignment of the title
   * @default "center"
   */
  titleAlign?: "left" | "center";
}

// ============================================
// COMPONENT
// ============================================

/**
 * CustomHeader - A full-width header component for modal/page headers
 *
 * Features:
 * - Back button on the left
 * - Centered title and optional subtitle
 * - Optional action button on the right
 * - Optional left content (icons, etc.) next to title
 * - Responsive design with proper text truncation
 * - Consistent styling with design system colors
 *
 * @example
 * // Basic usage
 * <CustomHeader
 *   title="Page Title"
 *   onBack={() => navigate(-1)}
 * />
 *
 * @example
 * // With action button
 * <CustomHeader
 *   title="Edit User"
 *   subtitle="john@example.com"
 *   onBack={handleClose}
 *   onAction={handleSave}
 *   actionLabel="Save"
 *   isLoading={isSaving}
 * />
 *
 * @example
 * // With custom left content
 * <CustomHeader
 *   title="Create Campaign"
 *   leftContent={<WhatsAppIcon size={18} />}
 *   onBack={handleBack}
 *   onAction={handleContinue}
 *   actionLabel="Continue"
 * />
 */
export const CustomHeader: React.FC<CustomHeaderProps> = memo(
  ({
    title,
    subtitle,
    onBack,
    backLabel = "Back",
    showBackButton = true,
    backIcon,
    onAction,
    actionLabel,
    actionIcon,
    actionVariant = "teal",
    isLoading = false,
    isActionDisabled = false,
    isBackDisabled = false,
    actionButtons,
    leftContent,
    className,
    style,
    backgroundColor = colors.primarySlate,
    balanceLayout = true,
    buttonMinWidth = "80px",
    titleAlign = "center",
  }) => {
    const screenSize = useResponsiveHeader();
    const isMobile = screenSize === "mobile";
    const isTablet = screenSize === "tablet";

    // Determine if we should show the action button (single action mode)
    const showSingleActionButton = Boolean(onAction && actionLabel);

    // Determine if we have multiple action buttons
    const hasMultipleButtons = actionButtons && actionButtons.length > 0;

    // Calculate button sizes based on screen size
    const getButtonSize = () => {
      if (isMobile) return "small";
      return "medium";
    };

    // Responsive button min-width classes
    const buttonMinWidthStyle = {
      minWidth: isMobile ? "60px" : buttonMinWidth,
    };

    const buttonMinWidthStyleLarge = {
      minWidth: "100px",
    };

    return (
      <div
        className={cn(
          "flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 shrink-0",
          className
        )}
        style={{
          backgroundColor,
          ...style,
        }}
      >
        {/* Left Section - Back Button */}
        <div className="shrink-0">
          {showBackButton ? (
            <Button
              variant="ghost"
              size={getButtonSize()}
              icon={backIcon ?? <ArrowLeft size={isMobile ? 14 : 16} />}
              onClick={onBack}
              disabled={isLoading || isBackDisabled}
              style={{ color: colors.white }}
            >
              <span className="hidden sm:inline">{backLabel}</span>
            </Button>
          ) : (
            <div
              style={buttonMinWidthStyle}
              className="hidden sm:block"
            />
          )}
        </div>

        {/* Center Section - Title and Subtitle */}
        <div className={cn("flex-1 min-w-0", titleAlign === "center" ? "text-center" : "text-left")}>
          <div className={cn("flex items-center gap-2 sm:gap-3", titleAlign === "center" ? "justify-center" : "justify-start")}>
            {leftContent && (
              <div className="shrink-0 flex items-center">{leftContent}</div>
            )}
            <div className="min-w-0 flex flex-col">
              {typeof title === "string" ? (
                <span
                  className={cn(
                    "font-semibold truncate block",
                    isMobile ? "text-sm" : "text-base"
                  )}
                  style={{ color: colors.white }}
                  title={title}
                >
                  {title}
                </span>
              ) : (
                <span>{title}</span>
              )}
              {subtitle && !isMobile && (
                <>
                  {typeof subtitle === "string" ? (
                    <span
                      className="text-xs truncate mt-0.5"
                      style={{ color: "rgba(255, 255, 255, 0.8)" }}
                    >
                      {subtitle}
                    </span>
                  ) : (
                    subtitle
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Section - Action Button(s) */}
        <div className="shrink-0">
          {hasMultipleButtons ? (
            // Multiple action buttons mode - responsive
            <div className="flex items-center gap-1 sm:gap-2">
              {actionButtons.map((button, index) => {
                // On mobile, only show last 2 buttons; on tablet show last 3
                const visibleOnMobile = index >= actionButtons.length - 2;
                const visibleOnTablet = index >= actionButtons.length - 3;

                return (
                  <Button
                    key={button.key}
                    variant={button.variant || "teal"}
                    size={getButtonSize()}
                    onClick={button.onClick}
                    loading={button.loading}
                    disabled={button.disabled || button.loading}
                    className={cn(
                      "text-xs sm:text-sm whitespace-nowrap",
                      // Hide early buttons on smaller screens
                      !visibleOnMobile && "hidden md:inline-flex",
                      visibleOnMobile && !visibleOnTablet && "hidden sm:inline-flex",
                      button.className
                    )}
                  >
                    {button.icon ? (
                      <span className="flex items-center gap-1">
                        <span className={cn(isMobile && "hidden sm:inline")}>
                          {button.label}
                        </span>
                        {button.icon}
                      </span>
                    ) : (
                      button.label
                    )}
                  </Button>
                );
              })}
            </div>
          ) : showSingleActionButton ? (
            // Single action button mode (backward compatible)
            <Button
              variant={actionVariant}
              size={getButtonSize()}
              onClick={onAction}
              loading={isLoading}
              disabled={isActionDisabled || isLoading}
              className="text-xs sm:text-sm whitespace-nowrap"
              style={{
                ...buttonMinWidthStyle,
                ...(screenSize === "desktop" ? buttonMinWidthStyleLarge : {}),
              }}
            >
              {actionIcon ? (
                <span className="flex items-center gap-1">
                  {actionLabel}
                  {actionIcon}
                </span>
              ) : (
                actionLabel
              )}
            </Button>
          ) : balanceLayout ? (
            <div
              style={buttonMinWidthStyle}
              className="hidden sm:block"
            />
          ) : null}
        </div>
      </div>
    );
  }
);

CustomHeader.displayName = "CustomHeader";

export default CustomHeader;
